import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { 
    GET_MAIN_CATEGORIES, 
    GET_SUB_CATEGORIES,
    CREATE_MAIN_CATEGORY, 
    CREATE_SUB_CATEGORY,
    UPDATE_MAIN_CATEGORY, 
    UPDATE_SUB_CATEGORY,
    DELETE_MAIN_CATEGORY, 
    DELETE_SUB_CATEGORY,
    UPDATE_MAIN_CATEGORY_ORDERS,
    UPDATE_SUB_CATEGORY_ORDERS
} from '../../apollo/queries';
import { useAuth } from '../AuthContext';
import '../css/CategoryManagementPage.css';

// DnD Kit imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component (활성/비활성 표시, 비활성화·활성화 버튼)
const SortableCategoryItem = ({ category, index, onEdit, onDelete, onActivate, isSubCategory = false, mainCategoryIndex = null }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isActive = category.isActive !== false;

    // 제품 개수 표시 로직
    const getDisplayName = () => {
        if (isSubCategory) {
            const productCount = category.productCount || 0;
            return `${category.name} (${productCount})`;
        } else {
            const subCategoryCount = category.subCategories?.length || 0;
            return `001 ${category.name} (${subCategoryCount})`;
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`category-row ${isSubCategory ? 'sub-category-row' : 'main-category-row'} sortable-item ${!isActive ? 'category-inactive' : ''}`}
            {...attributes}
        >
            <div className="category-info">
                <div className="drag-handle" {...listeners}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 6h8v2H8V6zm0 4h8v2H8v-2zm0 4h8v2H8v-2z" fill="#666"/>
                    </svg>
                </div>
                <span className="category-number">{isSubCategory ? `${mainCategoryIndex + 1}-${index + 1}` : index + 1}</span>
                <span className="category-name">
                    {getDisplayName()}
                </span>
                <span className={`category-status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                    {isActive ? '활성' : '비활성'}
                </span>
            </div>
            <div className="category-actions">
                <span onClick={() => onEdit(category, isSubCategory ? 'sub' : 'main')} className="action-btn">수정</span>
                <span className="separator">|</span>
                {isActive ? (
                    <span onClick={() => onDelete(category, isSubCategory ? 'sub' : 'main')} className="action-btn action-inactive">비활성화</span>
                ) : (
                    <span onClick={() => onActivate(category, isSubCategory ? 'sub' : 'main')} className="action-btn action-active">활성화</span>
                )}
            </div>
        </div>
    );
};

const CategoryManagementPage = () => {
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [categoryType, setCategoryType] = useState('main'); // 'main' 또는 'sub'

    // 폼 상태
    const [formData, setFormData] = useState({
        mainCategoryId: '',
        name: '',
        description: '',
        bannerImage: '',
        bannerUrl: '',
        order: 0
    });

    // DnD 센서 설정
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // GraphQL 쿼리 (관리자: 비활성 포함 전체 조회)
    const { data: mainCategoriesData, loading: mainCategoriesLoading, error: mainCategoriesError, refetch: refetchMainCategories } = useQuery(GET_MAIN_CATEGORIES, {
        variables: { includeInactive: true },
        errorPolicy: 'all'
    });

    const { data: subCategoriesData, loading: subCategoriesLoading, error: subCategoriesError, refetch: refetchSubCategories } = useQuery(GET_SUB_CATEGORIES, {
        variables: { includeInactive: true },
        errorPolicy: 'all'
    });

    // 뮤테이션들
    const [createMainCategory] = useMutation(CREATE_MAIN_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('메인 카테고리 생성 에러:', error);
        }
    });

    const [createSubCategory] = useMutation(CREATE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('하위 카테고리 생성 에러:', error);
        }
    });

    const [updateMainCategory] = useMutation(UPDATE_MAIN_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('메인 카테고리 업데이트 에러:', error);
        }
    });

    const [updateSubCategory] = useMutation(UPDATE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('하위 카테고리 업데이트 에러:', error);
        }
    });

    const [deleteMainCategory] = useMutation(DELETE_MAIN_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('메인 카테고리 삭제 에러:', error);
        }
    });

    const [deleteSubCategory] = useMutation(DELETE_SUB_CATEGORY, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('하위 카테고리 삭제 에러:', error);
        }
    });

    // 드래그 앤 드롭 뮤테이션들
    const [updateMainCategoryOrders] = useMutation(UPDATE_MAIN_CATEGORY_ORDERS, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('메인 카테고리 순서 업데이트 에러:', error);
            alert('카테고리 순서 업데이트 중 오류가 발생했습니다.');
        }
    });

    const [updateSubCategoryOrders] = useMutation(UPDATE_SUB_CATEGORY_ORDERS, {
        onCompleted: () => {
            refetchMainCategories();
            refetchSubCategories();
        },
        onError: (error) => {
            console.error('하위 카테고리 순서 업데이트 에러:', error);
            alert('하위 카테고리 순서 업데이트 중 오류가 발생했습니다.');
        }
    });

    // 데이터 준비
    const mainCategories = mainCategoriesData?.mainCategories || [];
    const subCategories = subCategoriesData?.subCategories || [];

    // 디버깅: 데이터 구조 확인
    console.log('Main Categories with product counts:', mainCategories);

    // 카테고리 구조화 (기존 코드와 호환)
    const organizedCategories = mainCategories.map(mainCat => ({
        ...mainCat,
        children: mainCat.subCategories || []
    }));

    // 드래그 앤 드롭 핸들러
    const handleMainCategoryDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = mainCategories.findIndex(cat => cat.id === active.id);
            const newIndex = mainCategories.findIndex(cat => cat.id === over.id);

            const reorderedCategories = arrayMove(mainCategories, oldIndex, newIndex);
            const categoryOrders = reorderedCategories.map((cat, index) => ({
                id: cat.id,
                order: index
            }));

            updateMainCategoryOrders({
                variables: { categoryOrders }
            });
        }
    };

    const handleSubCategoryDragEnd = (event, mainCategoryId) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const mainCategory = mainCategories.find(cat => cat.id === mainCategoryId);
            const subCategories = mainCategory?.subCategories || [];
            
            const oldIndex = subCategories.findIndex(cat => cat.id === active.id);
            const newIndex = subCategories.findIndex(cat => cat.id === over.id);

            const reorderedSubCategories = arrayMove(subCategories, oldIndex, newIndex);
            const categoryOrders = reorderedSubCategories.map((cat, index) => ({
                id: cat.id,
                order: index
            }));

            updateSubCategoryOrders({
                variables: { categoryOrders }
            });
        }
    };

    // 카테고리 편집 핸들러
    const handleCategoryEdit = (category, type) => {
        setSelectedCategory(category);
        setCategoryType(type);
        setFormData({
            mainCategoryId: type === 'sub' ? category.mainCategoryId : '',
            name: category.name,
            description: category.description || '',
            bannerImage: category.bannerImage || '',
            bannerUrl: category.bannerUrl || '',
            order: category.order
        });
        // 수정 시에는 모달을 열지 않고 폼에 데이터만 설정
        console.log('카테고리 편집 모드 활성화:', category.name);
    };

    // 카테고리 비활성화 핸들러 (실제 삭제 아님, 프론트 노출에서만 제외)
    const handleDeleteClick = (category, type) => {
        if (window.confirm(`이 ${type === 'main' ? '메인' : '하위'} 카테고리를 비활성화하시겠습니까?\n비활성화 시 쇼핑몰 화면에 표시되지 않습니다.`)) {
            if (type === 'main') {
                deleteMainCategory({
                    variables: { id: category.id }
                });
            } else {
                deleteSubCategory({
                    variables: { id: category.id }
                });
            }
        }
    };

    // 카테고리 활성화 핸들러
    const handleActivateClick = (category, type) => {
        const baseInput = {
            name: category.name,
            description: category.description ?? '',
            bannerImage: category.bannerImage ?? '',
            bannerUrl: category.bannerUrl ?? '',
            order: category.order ?? 0,
            isActive: true
        };
        if (type === 'main') {
            updateMainCategory({
                variables: { id: category.id, input: baseInput }
            });
        } else {
            updateSubCategory({
                variables: {
                    id: category.id,
                    input: { ...baseInput, mainCategoryId: category.mainCategoryId }
                }
            });
        }
    };

    // 폼 제출 핸들러
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsFormLoading(true);
        setFormErrors({});

        try {
            const input = {
                name: formData.name,
                description: formData.description || null,
                bannerImage: formData.bannerImage || null,
                bannerUrl: formData.bannerUrl || null,
                order: parseInt(formData.order) || 0,
                isActive: true
            };

            if (categoryType === 'sub') {
                input.mainCategoryId = formData.mainCategoryId;
            }

            if (selectedCategory) {
                // 수정
                if (categoryType === 'main') {
                    await updateMainCategory({
                        variables: { id: selectedCategory.id, input }
                    });
                } else {
                    await updateSubCategory({
                        variables: { id: selectedCategory.id, input }
                    });
                }
                alert(`${categoryType === 'main' ? '메인' : '하위'} 카테고리가 성공적으로 수정되었습니다.`);
            } else {
                // 생성
                if (categoryType === 'main') {
                    await createMainCategory({
                        variables: { input }
                    });
                } else {
                    await createSubCategory({
                        variables: { input }
                    });
                }
                alert(`${categoryType === 'main' ? '메인' : '하위'} 카테고리가 성공적으로 생성되었습니다.`);
            }

            resetForm();
        } catch (error) {
            const errorMessage = error.graphQLErrors?.[0]?.message || '처리 중 오류가 발생했습니다.';
            setFormErrors({ general: errorMessage });
        } finally {
            setIsFormLoading(false);
        }
    };

    // 폼 리셋
    const resetForm = () => {
        setFormData({
            mainCategoryId: '',
            name: '',
            description: '',
            bannerImage: '',
            bannerUrl: '',
            order: 0
        });
        setSelectedCategory(null);
        setCategoryType('main');
        setIsModalOpen(false);
        setFormErrors({});
    };

    // 폼 입력 핸들러
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (!isAdmin) {
        return <div>관리자 권한이 필요합니다.</div>;
    }

    if (mainCategoriesLoading || subCategoriesLoading) {
        return <div>로딩 중...</div>;
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 카테고리 관리</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>카테고리 관리</h3>
                        <ul>
                            <li className="active">카테고리 관리</li>
                            <li onClick={() => navigate('/admin/category-ranking')}>카테고리 순위</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-category">
                    <div className="category-form-container">
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <label>카테고리 선택</label>
                                <div className="category-selectors">
                                    <select 
                                        value={categoryType} 
                                        onChange={(e) => setCategoryType(e.target.value)}
                                    >
                                        <option value="main">메인 카테고리</option>
                                        <option value="sub">하위 카테고리</option>
                                    </select>
                                    {categoryType === 'sub' && (
                                        <select 
                                            name="mainCategoryId"
                                            value={formData.mainCategoryId}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">메인 카테고리 선택</option>
                                            {mainCategories.filter(cat => cat.isActive !== false).map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <label>카테고리명</label>
                                <input 
                                    type="text" 
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="카테고리명을 입력하세요"
                                    required
                                />
                            </div>
                            
                            <div className="form-row">
                                <label>설명</label>
                                <input 
                                    type="text" 
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="카테고리 설명을 입력하세요"
                                />
                            </div>
                            
                            <div className="form-row">
                                <label>배너 이미지</label>
                                <input 
                                    type="text" 
                                    name="bannerImage"
                                    value={formData.bannerImage}
                                    onChange={handleInputChange}
                                    placeholder="배너 이미지 URL을 입력하세요"
                                />
                            </div>
                            
                            <div className="form-row">
                                <label>배너 링크</label>
                                <input 
                                    type="text" 
                                    name="bannerUrl"
                                    value={formData.bannerUrl}
                                    onChange={handleInputChange}
                                    placeholder="배너 링크 URL을 입력하세요"
                                />
                            </div>

                            {formErrors.general && (
                                <div style={{ color: 'red', marginBottom: '20px' }}>
                                    {formErrors.general}
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="button" onClick={resetForm} className="btn-back">
                                    취소
                                </button>
                                <button type="submit" disabled={isFormLoading} className="btn-save">
                                    {isFormLoading ? '처리중...' : (selectedCategory ? '수정' : '등록')}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="category-list-container">
                        
                        <div className="category-list">
                            {organizedCategories.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                                    등록된 카테고리가 없습니다.
                                </div>
                            ) : (
                                <div className="category-items">
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleMainCategoryDragEnd}
                                    >
                                        <SortableContext
                                            items={mainCategories.map(cat => cat.id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {organizedCategories.map((cat, index) => (
                                                <div key={cat.id}>
                                                    {/* 메인 카테고리 */}
                                                    <SortableCategoryItem
                                                        category={cat}
                                                        index={index}
                                                        onEdit={handleCategoryEdit}
                                                        onDelete={handleDeleteClick}
                                                        onActivate={handleActivateClick}
                                                        isSubCategory={false}
                                                    />
                                                    
                                                    {/* 서브 카테고리들 */}
                                                    {cat.children.length > 0 && (
                                                        <DndContext
                                                            sensors={sensors}
                                                            collisionDetection={closestCenter}
                                                            onDragEnd={(event) => handleSubCategoryDragEnd(event, cat.id)}
                                                        >
                                                            <SortableContext
                                                                items={cat.children.map(child => child.id)}
                                                                strategy={verticalListSortingStrategy}
                                                            >
                                                                {cat.children.map((child, subIndex) => (
                                                                    <SortableCategoryItem
                                                                        key={child.id}
                                                                        category={child}
                                                                        index={subIndex}
                                                                        onEdit={handleCategoryEdit}
                                                                        onDelete={handleDeleteClick}
                                                                        onActivate={handleActivateClick}
                                                                        isSubCategory={true}
                                                                        mainCategoryIndex={index}
                                                                    />
                                                                ))}
                                                            </SortableContext>
                                                        </DndContext>
                                                    )}
                                                </div>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {/* 삭제 확인 모달 */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>카테고리 삭제 확인</h3>
                        <p>
                            '{selectedCategory?.name}' {categoryType === 'main' ? '메인 카테고리' : '하위 카테고리'}를 삭제하시겠습니까?
                            <br />
                            {categoryType === 'main' && '메인 카테고리를 삭제하면 하위 카테고리도 함께 삭제됩니다.'}
                        </p>
                        <div className="modal-actions">
                            <button onClick={() => setIsModalOpen(false)}>취소</button>
                            <button onClick={() => {
                                if (selectedCategory) {
                                    if (categoryType === 'main') {
                                        deleteMainCategory({
                                            variables: { id: selectedCategory.id }
                                        });
                                    } else {
                                        deleteSubCategory({
                                            variables: { id: selectedCategory.id }
                                        });
                                    }
                                }
                                setIsModalOpen(false);
                            }} className="delete-btn">삭제</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CategoryManagementPage; 